import { useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useCollectionBinding } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface SlidePhoto {
  image?: string;
  original_url?: string;
  alt_text?: string;
}

// How long each slide is shown before cross-fading to the next (ms).
const SLIDE_INTERVAL = 5000;

// ─── Slot group definitions ───────────────────────────────────────────────────

const cta1Def: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Gallery' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const cta2Def: SlotGroupDefinition = {
  label: 'Secondary CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule A Visit' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function PinnedCallouts() {
  const cta1 = useSlotGroup('pinnedcallouts.cta1', cta1Def);
  const cta2 = useSlotGroup('pinnedcallouts.cta2', cta2Def);

  // Background slideshow — cross-fades through the property photo gallery.
  const slides = useCollectionBinding<SlidePhoto>('pinnedcallouts.slides', {
    collection: 'photo_gallery',
    defaultLimit: 6,
  })
    .map((p) => ({ src: p.image ?? p.original_url ?? '', alt: p.alt_text ?? '' }))
    .filter((s) => s.src);

  const [active, setActive] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % slides.length);
    }, SLIDE_INTERVAL);
    return () => window.clearInterval(id);
  }, [slides.length]);

  useRevealStyles();
  const contentRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="relative overflow-hidden min-h-[340px] md:min-h-[440px] flex items-center justify-center">
      {/* ── Background slideshow (z-0) ──
          Slides are stacked and cross-faded via opacity. */}
      <div className="absolute inset-0 z-0">
        {slides.length > 0 ? (
          slides.map((slide, i) => (
            <VibesitesImage
              key={slide.src}
              url={slide.src}
              alt={slide.alt}
              className="pinnedcallouts-slide absolute inset-0 w-full h-full object-cover object-center"
              style={{ opacity: i === active ? 1 : 0 }}
              sizes="100vw"
              priority={i === 0}
            />
          ))
        ) : (
          <div className="absolute inset-0 bg-[var(--vibe-color-primary)]" />
        )}
      </div>

      {/* ── Dark overlay (z-[1], above bg, below content) ── */}
      <div className="absolute inset-0 bg-black/55 z-[1]" />

      {/* ── Content (z-10) ── */}
      <div
        ref={contentRef}
        className="reveal-zoom-front relative z-10 text-center max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20"
      >
        {/* Subtitle */}
        <RichTextContent
          path="pinnedcallouts.subtitle"
          label="Subtitle"
          defaultValue="<p>Experience The Difference</p>"
          as="p"
          className="font-body text-base uppercase tracking-[0.3em] text-white/80 mb-4"
        />

        {/* Divider */}
        <div className="w-10 h-px bg-white/60 mx-auto mb-6" />

        {/* Title */}
        <RichTextContent
          path="pinnedcallouts.title"
          label="Title"
          defaultValue="<p>Live Where You Love</p>"
          as="h2"
          className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.05] tracking-wide mb-10"
        />

        {/* CTAs — inline-grid keeps both buttons the same width (wider label wins) */}
        <div className="inline-grid grid-cols-1 sm:grid-cols-2 gap-4 mx-auto justify-items-stretch">
          <EditableSlotGroup groupPath="pinnedcallouts.cta1" definition={cta1Def}>
            <a
              href={cta1.url || '/gallery'}
              target={cta1._target || '_self'}
              className="pinnedcallouts-cta pinnedcallouts-cta--light relative inline-flex w-full items-center justify-center gap-2.5 px-10 py-4 font-body text-base font-normal normal-case overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{cta1.label || 'View Gallery'}</span>
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="pinnedcallouts.cta2" definition={cta2Def}>
            <a
              href={cta2.url || '/schedule-a-tour'}
              target={cta2._target || '_self'}
              className="pinnedcallouts-cta pinnedcallouts-cta--light relative inline-flex w-full items-center justify-center gap-2.5 px-10 py-4 font-body text-base font-normal normal-case overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{cta2.label || 'Schedule A Visit'}</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Slideshow cross-fade */
        .pinnedcallouts-slide {
          transition: opacity 1.2s ease-in-out;
        }
        @media (prefers-reduced-motion: reduce) {
          .pinnedcallouts-slide {
            transition: none;
          }
        }
        /* Shared CTA base — pill buttons */
        .pinnedcallouts-cta {
          border-radius: 9999px;
          box-shadow: none;
        }
        .pinnedcallouts-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          transition: background 0.3s ease;
        }
        .pinnedcallouts-cta:hover {
          box-shadow: none;
          transform: translateY(-2px);
        }
        .pinnedcallouts-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
        /* Light variant — solid white fill with dark label */
        .pinnedcallouts-cta--light {
          background: #ffffff;
          border: none;
          color: var(--vibe-color-text, #1f2937);
        }
        .pinnedcallouts-cta--light::before {
          background: rgba(0, 0, 0, 0);
        }
        .pinnedcallouts-cta--light:hover {
          background: #ffffff;
        }
        .pinnedcallouts-cta--light:hover::before {
          background: rgba(0, 0, 0, 0.06);
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PinnedCallouts',
  displayName: 'Pinned Callouts',
  tokens: {
    'pinnedcallouts.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Experience The Difference</p>',
    },
    'pinnedcallouts.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Live Where You Love</p>',
    },
  },
  slotGroups: {
    'pinnedcallouts.cta1': {
      label: 'Primary CTA',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'View Gallery' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'pinnedcallouts.cta2': {
      label: 'Secondary CTA',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule A Visit' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'pinnedcallouts.slides': {
      label: 'Background Slideshow',
      collection: 'photo_gallery',
      defaultLimit: 6,
    },
  },
};

(PinnedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PinnedCallouts;
